import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get } from "lodash";

import Grid from "@material-ui/core/Grid";

import { setBreadcrumb } from "../../actions/common";
import NuvButton from "../Common/NuvButton";
import SectionTitle from "../Common/SectionTitle";
import Loader from "../Common/Loader";
import MessageDialog, { MessageStatus } from "../Common/MessageDialog";
import {
  getPrinterWidthConfig,
  refreshLabelPrinterDevices,
  resetPrinterConfiguration,
} from "../../utils/labels";
import colors from "../../assets/style/colors";

const styles = {
  mainContainer: {
    width: "100%",
    margin: "0 auto",
    padding: "24px 30px",
    boxSizing: "border-box",
  },
  explanation: {
    margin: "12px",
    maxWidth: "720px",
    color: colors.black54,
    fontSize: "0.875em",
    lineHeight: "1.5",
  },
  printerRow: {
    margin: "0 12px",
    padding: "12px 0",
    borderBottom: `solid 1px ${colors.borderGrey}`,
    fontSize: "0.875em",
  },
  printerName: {
    fontWeight: "500",
  },
  printerUid: {
    color: colors.black54,
    fontSize: "0.85em",
    wordBreak: "break-all",
  },
  printerWidth: {
    textAlign: "right",
  },
  notConfigured: {
    color: colors.black38,
    fontStyle: "italic",
  },
  emptyState: {
    margin: "12px",
    fontSize: "0.875em",
    color: colors.black54,
  },
  buttonContainer: {
    margin: "24px 12px 0 12px",
    display: "flex",
    gap: "16px",
  },
};

const PrinterReset = (props) => {
  const { classes, dispatch } = props;

  const [devices, setDevices] = React.useState([]);
  const [widthConfig, setWidthConfig] = React.useState({});
  const [busy, setBusy] = React.useState(true);
  const [message, setMessage] = React.useState();
  const [messageStatus, setMessageStatus] = React.useState();

  const runPrinterAction = React.useCallback(async (action, successMessage) => {
    setBusy(true);
    try {
      const detectedDevices = await action();
      setDevices(detectedDevices || []);
      setWidthConfig(getPrinterWidthConfig());
      if (successMessage) {
        setMessage(successMessage);
        setMessageStatus(MessageStatus.SUCCESS);
      }
    } catch (error) {
      console.log("Printer reset error: " + error.message);
      setDevices([]);
      setMessage(I18n.get("printer_reset_unavailable"));
      setMessageStatus(MessageStatus.ERROR);
    }
    setBusy(false);
  }, []);

  React.useEffect(() => {
    dispatch(setBreadcrumb([{ name: I18n.get("sidebar_printerReset") }]));
    runPrinterAction(refreshLabelPrinterDevices);
  }, [dispatch, runPrinterAction]);

  const labelWidthOf = (device) => {
    const width = widthConfig[device.uid];
    if (!width) {
      return (
        <span className={classes.notConfigured}>
          {I18n.get("printer_reset_not_configured")}
        </span>
      );
    }
    return I18n.get("printer_reset_width").replace("{width}", width);
  };

  return (
    <Grid container direction="column" alignItems="stretch">
      <Grid item xs className={classes.mainContainer + " mainContainer"}>
        <Grid container direction="column">
          <SectionTitle>{I18n.get("printer_reset_detected")}</SectionTitle>

          <Grid item className={classes.explanation}>
            {I18n.get("printer_reset_explanation")}
          </Grid>

          {busy && <Loader noDecoration message={I18n.get("printer_reset_detecting")} />}

          {!busy && devices.length < 1 && (
            <Grid item className={classes.emptyState}>
              {I18n.get("label_printer_not_found")}
            </Grid>
          )}

          {!busy &&
            devices.map((device) => (
              <Grid
                item
                key={device.uid}
                container
                alignItems="center"
                className={classes.printerRow}
              >
                <Grid item xs={8}>
                  <div className={classes.printerName}>{device.name}</div>
                  <div className={classes.printerUid}>{device.uid}</div>
                </Grid>
                <Grid item xs={4} className={classes.printerWidth}>
                  {labelWidthOf(device)}
                </Grid>
              </Grid>
            ))}

          <Grid item className={classes.buttonContainer}>
            <NuvButton
              variant="outlined"
              disabled={busy}
              onClick={() => runPrinterAction(refreshLabelPrinterDevices)}
            >
              {I18n.get("printer_reset_detect").toUpperCase()}
            </NuvButton>
            <NuvButton
              variant="contained"
              disabled={busy}
              onClick={() =>
                runPrinterAction(
                  resetPrinterConfiguration,
                  I18n.get("printer_reset_done")
                )
              }
            >
              {I18n.get("printer_reset_action").toUpperCase()}
            </NuvButton>
          </Grid>
        </Grid>
      </Grid>

      <MessageDialog
        open={Boolean(message)}
        title={I18n.get("sidebar_printerReset")}
        message={message}
        status={messageStatus}
        onClose={() => setMessage(undefined)}
      />
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(PrinterReset));
